import React, { useState } from 'react';
import { Button } from '@components/primitives/Button';
import { useAppointments } from '@features/appointments';
import { SettingsIcon } from '@icons/SettingsIcon';
import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';

interface CalendarSettingsMenuProps {
  variant?: 'icon' | 'fullWidth';
}

// Type guard for valid timeslot values
const isValidTimeslot = (value: number): value is 1 | 2 | 3 | 4 | 6 | 12 => {
  return [1, 2, 3, 4, 6, 12].includes(value);
};

// Utility function to calculate valid timeslots for given row duration and step
const calculateValidTimeslots = (rowDuration: number, step: number): 1 | 2 | 3 | 4 | 6 | 12 => {
  const timeslots = rowDuration / step;

  // Check if the combination is valid (results in a whole number)
  if (!Number.isInteger(timeslots)) {
    // For invalid combinations, adjust step to make it valid
    if (rowDuration === 15) {
      const result = rowDuration / 15; // Use 15 min step for 15 min rows
      return isValidTimeslot(result) ? result : 1;
    }
    // For other durations, find the closest valid step
    const rounded = Math.round(timeslots);
    return isValidTimeslot(rounded) ? rounded : 4;
  }

  return isValidTimeslot(timeslots) ? timeslots : 4;
};

// Utility function to get closest valid row duration
const getClosestValidDuration = (duration: number): number => {
  const validDurations = [15, 30, 60];
  return validDurations.reduce((prev, curr) =>
    Math.abs(curr - duration) < Math.abs(prev - duration) ? curr : prev,
  );
};

const CalendarSettingsMenu: React.FC<CalendarSettingsMenuProps> = ({ variant = 'icon' }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { calendarGranularity, setCalendarGranularity } = useAppointments();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Calculate the current row duration based on step and timeslots
  const currentRowDuration = calendarGranularity.step * calendarGranularity.timeslots;

  const handleRowDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rowDuration = parseInt(event.target.value, 10);
    let step = calendarGranularity.step;

    // Handle invalid combinations (e.g., 15 min rows with 10 min steps)
    if (rowDuration === 15 && step === 10) {
      step = 15; // Adjust to valid step
    }

    const timeslots = calculateValidTimeslots(rowDuration, step);

    setCalendarGranularity({
      step: step,
      timeslots: timeslots,
    });
  };

  const handleStepChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStep = parseInt(event.target.value, 10) as 5 | 10 | 15;
    const currentRowDuration = calendarGranularity.step * calendarGranularity.timeslots;

    // Maintain current row duration, or find closest valid one
    const targetRowDuration = getClosestValidDuration(currentRowDuration);
    const newTimeslots = calculateValidTimeslots(targetRowDuration, newStep);

    setCalendarGranularity({
      step: newStep,
      timeslots: newTimeslots,
    });
  };

  return (
    <>
      {variant === 'fullWidth' ? (
        <Button
          variant="outlined"
          onClick={handleClick}
          startIcon={<SettingsIcon />}
          fullWidth
          sx={{
            color: '#535976',
            borderColor: 'rgba(0, 13, 77, 0.45)',
            justifyContent: 'flex-start',
            px: 2,
            py: 1,
            '&:hover': {
              borderColor: '#535976',
              backgroundColor: 'rgba(83, 89, 118, 0.04)',
            },
          }}
        >
          Calendar Settings
        </Button>
      ) : (
        <Tooltip title="Settings" placement="bottom" arrow={false}>
          <IconButton
            onClick={handleClick}
            size="small"
            aria-label="Calendar settings"
            sx={{
              color: '#535976',
              '&:hover': {
                backgroundColor: 'rgba(83, 89, 118, 0.08)',
              },
            }}
          >
            <SettingsIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            minWidth: 220,
            mt: 1,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Settings
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Time slot rows
          </Typography>
          <RadioGroup value={currentRowDuration} onChange={handleRowDurationChange}>
            <FormControlLabel
              value={15}
              control={<Radio size="small" />}
              label="15 minutes"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <FormControlLabel
              value={30}
              control={<Radio size="small" />}
              label="30 minutes"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <FormControlLabel
              value={60}
              control={<Radio size="small" />}
              label="1 hour"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          </RadioGroup>
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Drag precision
          </Typography>
          <RadioGroup value={calendarGranularity.step} onChange={handleStepChange}>
            <FormControlLabel
              value={5}
              control={<Radio size="small" />}
              label="5 minutes"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <FormControlLabel
              value={10}
              control={<Radio size="small" />}
              label="10 minutes"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <FormControlLabel
              value={15}
              control={<Radio size="small" />}
              label="15 minutes"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          </RadioGroup>
        </Box>
      </Menu>
    </>
  );
};

export default CalendarSettingsMenu;
